import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { imgUrl } from '../utils/imgUrl';

const Navbar = ({ bannerActive = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { getCartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Loungers', path: '/products?category=loungers' },
    { name: 'Chairs', path: '/products?category=chairs' },
    { name: 'Stools and Benches', path: '/products?category=stools-benches' },
    { name: 'Tables', path: '/products?category=tables' },
    { name: 'Umbrellas', path: '/products?category=umbrellas' },
    { name: 'Accessories', path: '/products?category=accessories' },
    { name: 'Cold Plunges', path: '/products?category=frozin-400-products' },
    { name: 'Videos', path: '/videos' },
    { name: 'PR', path: '/pr' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav style={{ top: bannerActive ? '36px' : '0' }} className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white/80 backdrop-blur-sm'
    }`}>
      <div className="w-full px-4 sm:px-6 lg:px-6">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group flex-shrink-0">
            <div className="h-16 flex items-center overflow-hidden">
              <img
                src={imgUrl("https://customer-assets.emergentagent.com/job_2b9a73bd-ff0b-417b-bbf4-234b110c9b5c/artifacts/95usklie_ChatGPT%20Image%20Jul%205%2C%202026%2C%2012_53_06%20AM.png")}
                alt="Rezvani Furniture Studio"
                className="h-40 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1 flex-1 justify-center mx-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  location.pathname + location.search === link.path || 
                  (link.path.includes('?category=') && location.search.includes(link.path.split('?')[1]))
                    ? 'text-cyan-600 bg-cyan-50'
                    : 'text-gray-700 hover:text-cyan-600 hover:bg-gray-50'
                }`}
              >
                {link.logo ? (
                  <img src={link.logo} alt={link.name} className="h-16 w-auto object-contain" />
                ) : (
                  link.name
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4 flex-shrink-0">
            <Link
              to="/cart"
              className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label={`Shopping cart, ${getCartCount()} item${getCartCount() === 1 ? '' : 's'}`}
              data-testid="nav-cart-link"
            >
              <ShoppingCart size={24} className="text-gray-700" />
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Link>
            <Link
              to="/contact"
              className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                  location.pathname + location.search === link.path || 
                  (link.path.includes('?category=') && location.search.includes(link.path.split('?')[1]))
                    ? 'text-cyan-600 bg-cyan-50'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {link.logo ? (
                  <img src={link.logo} alt={link.name} className="h-16 w-auto object-contain" />
                ) : (
                  link.name
                )}
              </Link>
            ))}
            <Link
              to="/cart"
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
            >
              <span>Shopping Cart</span>
              {getCartCount() > 0 && (
                <span className="w-6 h-6 bg-cyan-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Link>
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className="block w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-center rounded-lg font-medium mt-4"
            >
              Get Quote
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
